import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { IBarListItem } from 'src/types/bar';
import { ICategoryListItem } from 'src/types/category';
import { ICommonItemTransactionListItem } from 'src/types/item';
import { ISupplierItem, ISupplierListItem } from 'src/types/supplier';

import axios from '../../utils/axios';

export interface ISupplyListItem extends IBarListItem {
  category: ICategoryListItem[];
}

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  suppliers: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    suppliers: ISupplierListItem[];
  };
  supplier: ISupplierItem | null;
  supplies: {
    error: string | undefined;
    status: 'idle' | 'loading' | 'success' | 'failure';
    items: ISupplyListItem[];
  };
  transactions: {
    status: 'idle' | 'loading' | 'success' | 'failure';
    error: string | undefined;
    transactions: {
      count: number;
      page: number;
      pageSize: number;
      pageCount: number;
      transactions: ICommonItemTransactionListItem[];
    };
  };
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  suppliers: {
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    suppliers: [],
  },
  supplier: null,
  supplies: {
    status: 'idle',
    error: undefined,
    items: [],
  },
  transactions: {
    status: 'idle',
    error: undefined,
    transactions: {
      count: 0,
      page: 0,
      pageSize: 0,
      pageCount: 0,
      transactions: [],
    },
  },
};

const suppliersSlice = createSlice({
  name: 'suppliers',
  initialState,
  reducers: {
    removeSupplier: (state, action) => {
      const { supplierId } = action.payload;

      const index = state.suppliers.suppliers.findIndex((supplier) => supplier._id === supplierId);

      if (index > -1) {
        action.payload.index = index;
        state.suppliers.suppliers = state.suppliers.suppliers.filter(
          (supplier) => supplier._id !== supplierId
        );
      }
    },
    rollbackSupplierDeletion: (state, action) => {
      const { supplier, index } = action.payload;

      state.suppliers.suppliers.splice(index, 0, supplier);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPaginatedSuppliers.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getPaginatedSuppliers.fulfilled, (state, action) => {
        state.status = 'success';
        state.suppliers = action.payload;
      })
      .addCase(getPaginatedSuppliers.rejected, (state, action) => {
        state.suppliers = initialState.suppliers;
        state.status = 'failure';
        state.error = action.error.message;
      })
      // Get User By ID
      .addCase(getSupplierById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getSupplierById.fulfilled, (state, action) => {
        state.status = 'success';
        state.supplier = action.payload;
        state.error = initialState.error;
      })
      .addCase(getSupplierById.rejected, (state, action) => {
        state.supplier = initialState.supplier;
        state.status = 'failure';
        state.error = action.error.message;
      })

      // get supplies by supplier

      .addCase(getInventoryItemsBySupplier.pending, (state) => {
        state.supplies.status = 'loading';
      })
      .addCase(getInventoryItemsBySupplier.fulfilled, (state, action) => {
        state.supplies.status = 'success';
        state.supplies.items = action.payload;
        state.supplies.error = undefined;
      })
      .addCase(getInventoryItemsBySupplier.rejected, (state, action) => {
        state.supplies.items = initialState.supplies.items;
        state.supplies.status = 'failure';
        state.supplies.error = action.error.message;
      })

      .addCase(getItemTransactionsBySupplier.pending, (state) => {
        state.transactions.status = 'loading';
      })
      .addCase(getItemTransactionsBySupplier.fulfilled, (state, action) => {
        state.transactions.status = 'success';
        state.transactions.transactions = action.payload;
      })
      .addCase(getItemTransactionsBySupplier.rejected, (state, action) => {
        state.transactions.status = 'failure';
        state.transactions.error = action.error.message;
      });
  },
});

export default suppliersSlice.reducer;

export const { removeSupplier, rollbackSupplierDeletion } = suppliersSlice.actions;

export const getPaginatedSuppliers = createAsyncThunk(
  'suppliers/paginated',
  async (params: {
    page: number | null;
    limit: number | null;
    generalSearch: string | null;
    supplierName?: string | null;
    contactPerson?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
    secondaryPhoneNumber?: string | null;
    categories?: string[] | null;
    categoriesSeparator?: string | null;
    sortBy: string | null;
    sort: 'asc' | 'desc' | null;
  }) => {
    try {
      const response = await axios.get(
        `supplier?page=${params.page !== null ? params.page + 1 : ''}&limit=${
          params.limit ?? ''
        }&generalSearch=${params.generalSearch ?? ''}&supplierName=${
          params.supplierName ?? ''
        }&contactPerson=${params.contactPerson ?? ''}&email=${params.email ?? ''}&phoneNumber=${
          params.phoneNumber ?? ''
        }&secondaryPhoneNumber=${params.secondaryPhoneNumber ?? ''}&status=active&sortBy=${
          params.sortBy
        }&sort=${params.sort}&categories=${
          params.categories ? params.categories.join(params.categoriesSeparator ?? ',') : ''
        }&createdBy=`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getSupplierById = createAsyncThunk(
  'suppliers/getSupplierById',
  async (params: { supplierID: string }) => {
    const { supplierID } = params;

    try {
      const response = await axios.get(`supplier/${supplierID}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const deleteSupplier = createAsyncThunk(
  'suppliers/deleteSupplier',
  async (params: { supplier: ISupplierListItem; index: number }, { dispatch }) => {
    const { supplier, index } = params;

    try {
      const response = await axios.delete(`supplier/${supplier._id}`);
      return response.data.data;
    } catch (err) {
      dispatch(rollbackSupplierDeletion({ supplier, index }));
      throw err;
    }
  }
);

export interface TCreateSupplier
  extends Omit<
    ISupplierItem,
    '_id' | 'createdAt' | 'status' | 'updatedAt' | 'inventoryItems' | 'category'
  > {
  category: string[];
}

export const createSupplier = createAsyncThunk(
  'suppliers/createSupplier',
  async (params: { supplier: TCreateSupplier }) => {
    const { supplier } = params;

    const response = await axios.post(`supplier`, supplier);

    return response.data.data;
  }
);

export const updateSupplier = createAsyncThunk(
  'suppliers/updateSupplier',
  async (params: { supplierId: string; supplier: TCreateSupplier }) => {
    const { supplier, supplierId } = params;
    const response = await axios.put(`/supplier/${supplierId}`, supplier);
    return response.data.data;
  }
);

export const getInventoryItemsBySupplier = createAsyncThunk(
  'suppliers/getInventoryItems',
  async (params: { supplierId: string }) => {
    const { supplierId } = params;
    try {
      const response = await axios.get(
        `/inventory/item/by-supplier/${supplierId}?page&limit&sortBy=itemName&sort=asc`
      );
      return response.data.data.items;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getItemTransactionsBySupplier = createAsyncThunk(
  'suppliers/getItemTransactionsBySupplier',
  async (params: {
    supplier: string;
    page: number;
    limit: number;
    sort: 'asc' | 'desc';
    sortBy: string;
  }) => {
    try {
      const { supplier, page, limit, sort, sortBy } = params;
      const response = await axios.get(
        `inventory/kitchen/transaction?page=${
          page + 1
        }&limit=${limit}&sortBy=${sortBy}&sort=${sort}&suppliers=${supplier}&status=completed&generalSearch=&transactionCode=&categories=&isDiscounted=&minTotalValue=&maxTotalValue=&transactionType=&paymentStatus=&updatedBy=&purchaseOrDispatchDate=&createdBy=`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);
